import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Layout, PageHero, CTA } from '@components';
import { services } from '@config';
import FeatureIcon from '@components/icons/feature';

const StyledList = styled.div`
  ${({ theme }) => theme.mixins.container};
  display: flex;
  flex-direction: column;
  gap: 26px;
  padding-top: 64px;
  padding-bottom: 64px;

  @media (max-width: 768px) {
    padding-top: 40px;
    padding-bottom: 40px;
  }
`;

const StyledItem = styled.section`
  ${({ theme }) => theme.mixins.card};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  padding: 44px;
  scroll-margin-top: calc(var(--nav-height) + 24px);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 28px;
    padding: 32px 26px;
  }

  .head {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .icon {
    ${({ theme }) => theme.mixins.flexCenter};
    width: 58px;
    height: 58px;
    margin-bottom: 22px;
    border-radius: 16px;
    color: #ffffff;
    background: var(--gradient);
    box-shadow: 0 18px 36px -20px rgba(86, 112, 246, 0.95);
  }

  h2 {
    margin: 0 0 14px;
    font-size: clamp(1.5rem, 2.6vw, 2rem);
  }

  .tagline {
    margin: 0 0 18px;
    color: var(--blue);
    font-size: var(--fz-md);
    font-weight: 600;
  }

  .description {
    margin: 0;
    color: var(--text-soft);
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 26px;
  }

  ul.features {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    gap: 12px;

    li {
      position: relative;
      padding-left: 26px;
      color: var(--text-soft);
      font-size: var(--fz-sm);
      line-height: 1.5;

      &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.5em;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--gradient);
      }
    }
  }

  .stack {
    padding-top: 22px;
    border-top: 1px solid var(--line);

    h4 {
      margin: 0 0 12px;
      color: var(--text-mute);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      font-weight: 600;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }

    ul {
      ${({ theme }) => theme.mixins.resetList};
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      li {
        padding: 7px 14px;
        border-radius: 999px;
        background: var(--paper-tint);
        color: var(--text-soft);
        font-family: var(--font-mono);
        font-size: var(--fz-xxs);
      }
    }
  }
`;

const ServicesPage = ({ location }) => (
  <Layout
    location={location}
    title="Услуги"
    description="Разработка сайтов, мобильных приложений и веб-сервисов, управление IT-проектами, искусственный интеллект и информационная безопасность.">
    <main>
      <PageHero
        eyebrow="Услуги"
        title="Разработка сайтов, приложений и веб-сервисов"
        lead="Полный цикл работ: от анализа требований и проектирования до разработки, контроля качества, запуска и сопровождения. Отдельное внимание — искусственному интеллекту и информационной безопасности."
      />

      <StyledList>
        {services.map(service => (
          <StyledItem key={service.icon} id={service.icon}>
            <div className="head">
              <span className="icon">
                <FeatureIcon name={service.icon} size={28} />
              </span>
              <h2>{service.title}</h2>
              <p className="tagline">{service.tagline}</p>
              <p className="description">{service.description}</p>
            </div>

            <div className="details">
              <ul className="features">
                {service.features.map(feature => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>

              <div className="stack">
                <h4>Технологии и подходы</h4>
                <ul>
                  {service.stack.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </StyledItem>
        ))}
      </StyledList>

      <CTA />
    </main>
  </Layout>
);

ServicesPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default ServicesPage;
